import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

const LoginPage = () => {
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        await register(email, password);
      }
      navigate(from, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const serverError = err?.response?.data?.error;
      if (mode === 'register' && status === 409) {
        setError('An account with that email already exists. Try logging in.');
      } else if (mode === 'register' && status === 400) {
        setError(serverError || 'Please enter a valid email and a password of at least 8 characters.');
      } else if (mode === 'login' && status === 401) {
        setError('Incorrect email or password.');
      } else {
        setError(serverError || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const toggleMode = () => {
    setMode((m) => (m === 'login' ? 'register' : 'login'));
    setError(null);
  };

  return (
    <div className="flex justify-center items-center min-h-[60vh]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-surface rounded-lg p-8 w-full max-w-md shadow-lg"
      >
        <h1 className="text-2xl font-bold text-center mb-2">
          {mode === 'login' ? 'Welcome back' : 'Create your account'}
        </h1>
        <p className="text-center text-gray-400 mb-6">
          {mode === 'login'
            ? 'Log in to play the arcade.'
            : 'Sign up to start playing.'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm text-gray-300 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 rounded bg-background border border-gray-600 text-white focus:outline-none focus:border-primary"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm text-gray-300 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 rounded bg-background border border-gray-600 text-white focus:outline-none focus:border-primary"
            />
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-900 bg-opacity-30 rounded px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary w-full disabled:opacity-60"
          >
            {submitting
              ? 'Please wait…'
              : mode === 'login'
              ? 'Log in'
              : 'Sign up'}
          </button>
        </form>

        <div className="text-center text-sm text-gray-400 mt-6">
          {mode === 'login' ? (
            <>
              Don&apos;t have an account?{' '}
              <button
                type="button"
                onClick={toggleMode}
                className="text-secondary hover:underline"
              >
                Sign up
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button
                type="button"
                onClick={toggleMode}
                className="text-secondary hover:underline"
              >
                Log in
              </button>
            </>
          )}
        </div>

        <div className="text-center mt-4">
          <Link to="/" className="text-xs text-gray-500 hover:text-secondary">
            Back to games
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
